import React, { useState, useEffect, useMemo } from 'react'
import { useI18n } from '../../i18n'
import { useAuth } from '../../components/AuthProvider'
import { Button } from '../../components/ui'
import { Select } from '../../components/ui/Select'
import { SearchField } from '../../components/ui/SearchField'
import { GovernanceListingSection } from '../../components/governance/GovernanceListingSection'
import { TableEmptyRow, TableErrorRow, TableLoadingRow } from '../../components/governance/TableBodyStates'
import listStyles from '@/styles/pages/governance/GovernanceListing.module.scss'
import { API_ENDPOINTS } from '../../config/api'
import CreatePolicyModal from '../../components/governance/CreatePolicyModal'
import CreateFromTemplateModal from '../../components/governance/CreateFromTemplateModal'
import UploadVersionModal from '../../components/governance/UploadVersionModal'
import { Plus, FileText, Calendar, Download, Edit, Trash2, Eye, X, Upload } from 'lucide-react'
import { PageLayout } from '../../layouts/PageLayout'
import { featureFlags } from '../../config/featureFlags'
import { PolicyDocumentUuidCell } from '../../components/policies/PolicyDocumentUuidCell'
import { PolicyDocumentVersionHistory } from '../../components/policies/PolicyDocumentVersionHistory'
import { PolicyDocumentEditModal } from '../../components/policies/PolicyDocumentEditModal'
import GovernanceDocumentRagPanel from '../../components/governance/GovernanceDocumentRagPanel'
import GovernanceDocumentIndexStatusBadge from '../../components/governance/GovernanceDocumentIndexStatusBadge'

interface Policy {
  id: string
  uuid?: string
  displayId?: string
  currentVersionId?: string | null
  title: string
  code?: string
  type: string
  status: string
  department?: string
  ownerName?: string
  ownerEmail?: string
  effectiveFrom?: string
  effectiveTo?: string
  createdAt: string
  updatedAt: string
  versions: Array<{
    id: string
    version: string
    createdAt: string
    changeLog?: string
    fileName?: string
    status?: string
    changeNote?: string
  }>
  reviews: Array<{
    id: string
    plannedDate: string
    status: string
    reviewerName?: string
  }>
  _count: {
    versions: number
    reviews: number
  }
}

const GovernancePolicies: React.FC = () => {
  const { t } = useI18n()
  const { token } = useAuth()
  const [policies, setPolicies] = useState<Policy[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [templateModalOpen, setTemplateModalOpen] = useState(false)
  const [selectedPolicy, setSelectedPolicy] = useState<Policy | null>(null)
  const [showVersions, setShowVersions] = useState<string | null>(null)
  const [showReviews, setShowReviews] = useState<string | null>(null)
  const [uploadVersionModalOpen, setUploadVersionModalOpen] = useState(false)
  const [uploadPolicyId, setUploadPolicyId] = useState<string | null>(null)
  const [uploadPolicyTitle, setUploadPolicyTitle] = useState('')
  const [versionHistoryByPolicy, setVersionHistoryByPolicy] = useState<Record<string, Policy['versions']>>({})
  const [editPolicy, setEditPolicy] = useState<Policy | null>(null)
  const [ragPolicyId, setRagPolicyId] = useState<string | null>(null)
  
  // Filters
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set())
  const [typeFilter, setTypeFilter] = useState<Set<string>>(new Set())
  const [departmentFilter, setDepartmentFilter] = useState<Set<string>>(new Set())
  const [searchQuery, setSearchQuery] = useState('')
  const [statusPickerKey, setStatusPickerKey] = useState(0)
  const [typePickerKey, setTypePickerKey] = useState(0)
  const [departmentPickerKey, setDepartmentPickerKey] = useState(0)

  useEffect(() => {
    loadPolicies()
  }, [])

  const loadPolicies = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = new URLSearchParams()
      if (statusFilter.size > 0) {
        Array.from(statusFilter).forEach(s => params.append('status', s))
      }
      if (typeFilter.size > 0) {
        Array.from(typeFilter).forEach(t => params.append('type', t))
      }
      if (departmentFilter.size > 0) {
        Array.from(departmentFilter).forEach(d => params.append('department', d))
      }
      if (searchQuery) {
        params.append('search', searchQuery)
      }

      const url = `${API_ENDPOINTS.GOVERNANCE_POLICIES}${params.toString() ? `?${params.toString()}` : ''}`
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!res.ok) throw new Error('Failed to load policies')
      const data = await res.json()
      setPolicies(data.policies || [])
    } catch (err: any) {
      setError(err.message || 'Failed to load policies')
      console.error('[GovernancePolicies] Error:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadPolicies()
  }, [statusFilter, typeFilter, departmentFilter, searchQuery])

  useEffect(() => {
    if (!showVersions || !token) return
    const policyId = showVersions
    if (versionHistoryByPolicy[policyId]) return
    fetch(API_ENDPOINTS.GOVERNANCE_POLICY_VERSIONS(policyId), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.versions) {
          setVersionHistoryByPolicy((prev) => ({ ...prev, [policyId]: data.versions }))
        }
      })
      .catch(() => {})
  }, [showVersions, token, versionHistoryByPolicy])

  const handleCreatePolicy = async (formData: any) => {
    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_POLICIES, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to create policy')
      }

      await loadPolicies()
    } catch (err: any) {
      throw err
    }
  }

  const handleDeletePolicy = async (id: string) => {
    if (!confirm(t('governance.policies.deleteConfirm', 'Are you sure you want to delete this policy?'))) {
      return
    }

    try {
      const res = await fetch(API_ENDPOINTS.GOVERNANCE_POLICY(id), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!res.ok) {
        const data = await res.json()
        throw new Error(data.error || 'Failed to delete policy')
      }

      await loadPolicies()
    } catch (err: any) {
      alert(err.message || t('governance.policies.deleteError', 'Failed to delete policy'))
    }
  }

  const handleDownloadVersion = async (policyId: string, versionId: string) => {
    try {
      const url = API_ENDPOINTS.GOVERNANCE_POLICY_VERSION_DOWNLOAD(policyId, versionId)
      const res = await fetch(url, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!res.ok) throw new Error('Failed to download version')

      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = downloadUrl
      a.download = `policy-version-${versionId}.pdf`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (err: any) {
      alert(err.message || t('governance.policies.downloadError', 'Failed to download version'))
    }
  }

  // Calculate overview metrics
  const overview = useMemo(() => {
    const active = policies.filter(p => p.status === 'Approved').length
    const underReview = policies.filter(p => p.status === 'UnderReview').length
    const deprecated = policies.filter(p => p.status === 'Deprecated').length
    const dueSoon = policies.filter(p => {
      const nextReview = p.reviews?.[0]
      if (!nextReview || nextReview.status !== 'Planned') return false
      const reviewDate = new Date(nextReview.plannedDate)
      const daysUntil = Math.ceil((reviewDate.getTime() - Date.now()) / (1000 * 60 * 60 * 24))
      return daysUntil <= 30 && daysUntil >= 0
    }).length

    return { active, underReview, deprecated, dueSoon }
  }, [policies])

  // Get unique values for filters
  const availableStatuses = useMemo(() => {
    return Array.from(new Set(policies.map(p => p.status))).sort()
  }, [policies])

  const availableTypes = useMemo(() => {
    return Array.from(new Set(policies.map(p => p.type))).sort()
  }, [policies])

  const availableDepartments = useMemo(() => {
    return Array.from(new Set(policies.map(p => p.department).filter(Boolean))).sort()
  }, [policies])

  const statusFilterAddOptions = useMemo(
    () => availableStatuses.map((status) => ({ value: status, label: status })),
    [availableStatuses],
  )

  const typeFilterAddOptions = useMemo(() => {
    const labels: Record<string, string> = {
      policy: t('governance.policies.form.typePolicy', 'Policy'),
      procedure: t('governance.policies.form.typeProcedure', 'Procedure'),
      guideline: t('governance.policies.form.typeGuideline', 'Guideline'),
      instruction: t('governance.policies.form.typeInstruction', 'Instruction'),
    }
    return availableTypes.map((type) => ({ value: type, label: labels[type] || type }))
  }, [availableTypes, t])

  const departmentFilterAddOptions = useMemo(
    () => availableDepartments.map((d) => ({ value: d as string, label: d as string })),
    [availableDepartments],
  )

  const getPolicyStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'Approved':
        return listStyles.badgePolicyApproved
      case 'UnderReview':
        return listStyles.badgePolicyUnderReview
      case 'Draft':
        return listStyles.badgePolicyDraft
      case 'Deprecated':
        return listStyles.badgePolicyDeprecated
      default:
        return listStyles.badgeDefault
    }
  }

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      policy: t('governance.policies.form.typePolicy', 'Policy'),
      procedure: t('governance.policies.form.typeProcedure', 'Procedure'),
      guideline: t('governance.policies.form.typeGuideline', 'Guideline'),
      instruction: t('governance.policies.form.typeInstruction', 'Instruction')
    }
    return labels[type] || type
  }

  return (
    <PageLayout
      pageScope="governance-policies-page"
      title={t(
        'governance.policies.titleGovernance',
        featureFlags.DOCUMENT_LIFECYCLE_V1 ? 'Policies & Governance Library' : 'Security Policies Library',
      )}
      actions={
        <div className={listStyles.headerActions}>
          <Button
            type="button"
            size="small"
            color="primary"
            icon={<FileText className={listStyles.badgeIcon} aria-hidden />}
            onClick={() => setTemplateModalOpen(true)}
          >
            {t('governance.policies.createFromTemplate', 'Create from Template')}
          </Button>
          <Button
            type="button"
            size="small"
            color="primary"
            icon={<Plus className={listStyles.badgeIcon} aria-hidden />}
            onClick={() => setCreateModalOpen(true)}
            className={listStyles.btnGreen}
          >
            {t('governance.policies.create', 'Create New Policy')}
          </Button>
        </div>
      }
    >
      <GovernanceListingSection title={t('governance.policies.overview', 'Policy Overview')}>
        <div className={`${listStyles.metricsGrid} ${listStyles.metricsGrid4}`}>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileBlue}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.policies.active', 'Active')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textBlue}`}>{overview.active}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileYellow}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.policies.underReview', 'Under Review')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textYellow}`}>{overview.underReview}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileRed}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.policies.deprecated', 'Deprecated')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textRed}`}>{overview.deprecated}</p>
          </div>
          <div className={`${listStyles.metricTile} ${listStyles.metricTileOrange}`}>
            <p className={listStyles.metricTileLabel}>{t('governance.policies.dueSoon', 'Review Due Soon')}</p>
            <p className={`${listStyles.metricTileValue} ${listStyles.textOrange}`}>{overview.dueSoon}</p>
          </div>
        </div>
      </GovernanceListingSection>

      <GovernanceListingSection title={t('governance.policies.documentAnalysis', 'Policy document analysis (RAG)')}>
        {ragPolicyId ? (
          <GovernanceDocumentRagPanel
            title={t('governance.policies.analyzeConsistency', 'Analyze policy consistency')}
            intent="policy_consistency_analysis"
            policyIds={[ragPolicyId]}
            presetQueries={[
              {
                label: t('governance.policies.consistency', 'Policy consistency'),
                query: 'Assess internal consistency of this policy against linked controls. Cite excerpts only.',
              },
              {
                label: t('governance.policies.relatedControls', 'Related controls'),
                query: 'Which governance controls are referenced or implied? Cite document chunks only.',
              },
            ]}
          />
        ) : (
          <p className="text-sm text-slate-500 m-0">
            {t('governance.policies.selectRowForRag', 'Select a policy row to run citation-grounded analysis.')}
          </p>
        )}
      </GovernanceListingSection>

      <GovernanceListingSection
        title={t('governance.policies.list', 'Policies List')}
        description={t(
          'governance.policies.listSubtitle',
          'Add filters below, then manage versions, downloads, and reviews.',
        )}
        toolbar={
          <div className={listStyles.panelToolbarFull}>
            <div className={listStyles.filtersRow}>
              <div className={`${listStyles.toolbarField} ${listStyles.filtersToolbarGrow}`}>
                <SearchField
                  label={t('governance.policies.searchLabel', 'Search')}
                  value={searchQuery}
                  onChange={setSearchQuery}
                  placeholder={t('governance.policies.searchPlaceholder', 'Search policies...')}
                  minWidth="14rem"
                />
              </div>
              <div className={listStyles.toolbarField}>
                <Select<string>
                  key={statusPickerKey}
                  label={t('governance.policies.addStatus', 'Add status')}
                  minWidth="10rem"
                  placeholder={t('governance.policies.filterStatus', 'Status')}
                  options={statusFilterAddOptions}
                  value={null}
                  disabled={statusFilterAddOptions.length === 0}
                  onChange={(v) => {
                    if (v) {
                      setStatusFilter((prev) => new Set([...prev, v]))
                      setStatusPickerKey((k) => k + 1)
                    }
                  }}
                />
              </div>
              <div className={listStyles.toolbarField}>
                <Select<string>
                  key={typePickerKey}
                  label={t('governance.policies.addType', 'Add type')}
                  minWidth="10rem"
                  placeholder={t('governance.policies.filterType', 'Type')}
                  options={typeFilterAddOptions}
                  value={null}
                  disabled={typeFilterAddOptions.length === 0}
                  onChange={(v) => {
                    if (v) {
                      setTypeFilter((prev) => new Set([...prev, v]))
                      setTypePickerKey((k) => k + 1)
                    }
                  }}
                />
              </div>
              {availableDepartments.length > 0 && (
                <div className={listStyles.toolbarField}>
                  <Select<string>
                    key={departmentPickerKey}
                    label={t('governance.policies.addDepartment', 'Add department')}
                    minWidth="10rem"
                    placeholder={t('governance.policies.filterDepartment', 'Department')}
                    options={departmentFilterAddOptions}
                    value={null}
                    disabled={departmentFilterAddOptions.length === 0}
                    onChange={(v) => {
                      if (v) {
                        setDepartmentFilter((prev) => new Set([...prev, v]))
                        setDepartmentPickerKey((k) => k + 1)
                      }
                    }}
                  />
                </div>
              )}
            </div>
            {(statusFilter.size > 0 || typeFilter.size > 0 || departmentFilter.size > 0) && (
              <div className={listStyles.filterChips}>
                {Array.from(statusFilter).map((status) => (
                  <span key={`status-${status}`} className={`${listStyles.filterChip} ${listStyles.filterChipBlue}`}>
                    {status}
                    <Button
                      type="button"
                      size="small"
                      color="outline"
                      iconOnly
                      icon={<X className={listStyles.badgeIcon} aria-hidden />}
                      onClick={() =>
                        setStatusFilter((prev) => {
                          const next = new Set(prev)
                          next.delete(status)
                          return next
                        })
                      }
                      className="border-0 bg-transparent p-0 shadow-none hover:text-red-600"
                    />
                  </span>
                ))}
                {Array.from(typeFilter).map((type) => (
                  <span key={`type-${type}`} className={`${listStyles.filterChip} ${listStyles.filterChipGreen}`}>
                    {getTypeLabel(type)}
                    <Button
                      type="button"
                      size="small"
                      color="outline"
                      iconOnly
                      icon={<X className={listStyles.badgeIcon} aria-hidden />}
                      onClick={() =>
                        setTypeFilter((prev) => {
                          const next = new Set(prev)
                          next.delete(type)
                          return next
                        })
                      }
                      className="border-0 bg-transparent p-0 shadow-none hover:text-red-600"
                    />
                  </span>
                ))}
                {Array.from(departmentFilter).map((dept) => (
                  <span key={`dept-${dept}`} className={`${listStyles.filterChip} ${listStyles.filterChipPurple}`}>
                    {dept}
                    <Button
                      type="button"
                      size="small"
                      color="outline"
                      iconOnly
                      icon={<X className={listStyles.badgeIcon} aria-hidden />}
                      onClick={() =>
                        setDepartmentFilter((prev) => {
                          const next = new Set(prev)
                          next.delete(dept)
                          return next
                        })
                      }
                      className="border-0 bg-transparent p-0 shadow-none hover:text-red-600"
                    />
                  </span>
                ))}
              </div>
            )}
          </div>
        }
      >
        {createModalOpen ? (
          <CreatePolicyModal
            open={createModalOpen}
            onClose={() => setCreateModalOpen(false)}
            onSubmit={handleCreatePolicy}
          />
        ) : null}
        {templateModalOpen ? (
          <CreateFromTemplateModal
            open={templateModalOpen}
            onClose={() => setTemplateModalOpen(false)}
            onSuccess={loadPolicies}
          />
        ) : null}
        {editPolicy ? (
          <PolicyDocumentEditModal
            open={Boolean(editPolicy)}
            policy={editPolicy}
            onClose={() => setEditPolicy(null)}
            onSuccess={() => {
              setEditPolicy(null)
              setVersionHistoryByPolicy({})
              void loadPolicies()
            }}
          />
        ) : null}
        <div className={listStyles.tableWrap}>
          <table className={listStyles.table}>
            <thead>
              <tr>
                {featureFlags.DOCUMENT_LIFECYCLE_V1 ? (
                  <th className={listStyles.th} scope="col">
                    {t('governance.documentLifecycle.documentUuid', 'Document UUID')}
                  </th>
                ) : null}
                <th className={listStyles.th} scope="col">
                  {t('governance.policies.table.title', 'Title')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.policies.table.code', 'Code')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.policies.table.type', 'Type')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.policies.table.status', 'Status')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.policies.table.department', 'Department')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.policies.table.owner', 'Owner')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.policies.table.effectiveFrom', 'Effective From')}
                </th>
                <th className={listStyles.th} scope="col">
                  {t('governance.policies.table.nextReview', 'Next Review')}
                </th>
                {featureFlags.DOCUMENT_LIFECYCLE_V1 ? (
                  <th className={listStyles.th} scope="col">
                    {t('governance.documentLifecycle.effectiveTo', 'Effective To')}
                  </th>
                ) : null}
                <th className={listStyles.th} scope="col">
                  {t('governance.policies.table.actions', 'Actions')}
                </th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <TableLoadingRow colSpan={featureFlags.DOCUMENT_LIFECYCLE_V1 ? 11 : 9}>
                  {t('common.loading', 'Loading...')}
                </TableLoadingRow>
              )}
              {!loading && error && (
                <TableErrorRow colSpan={featureFlags.DOCUMENT_LIFECYCLE_V1 ? 11 : 9}>{error}</TableErrorRow>
              )}
              {!loading && !error && policies.length === 0 && (
                <TableEmptyRow colSpan={featureFlags.DOCUMENT_LIFECYCLE_V1 ? 11 : 9}>
                  {t('governance.policies.noPolicies', 'No policies found. Create your first policy to get started.')}
                </TableEmptyRow>
              )}
              {!loading &&
                !error &&
                policies.map((policy) => {
                  const nextReview = policy.reviews?.[0]
                  const effectiveToDate =
                    (policy as { effectiveToDisplay?: string }).effectiveToDisplay ||
                    policy.effectiveTo ||
                    nextReview?.plannedDate
                  return (
                    <React.Fragment key={policy.id}>
                      <tr
                        className={`${listStyles.row} ${ragPolicyId === policy.id ? 'bg-blue-50' : ''}`}
                        onClick={() => setRagPolicyId(policy.id)}
                      >
                        {featureFlags.DOCUMENT_LIFECYCLE_V1 ? (
                          <td className={listStyles.td}>
                            <PolicyDocumentUuidCell uuid={policy.uuid || policy.id} />
                          </td>
                        ) : null}
                        <td className={listStyles.td}>
                          <div className={listStyles.cellStrong}>{policy.title}</div>
                            {policy.versions.length > 0 && (
                              <Button
                                type="button"
                                size="small"
                                color="outline"
                                onClick={() => setShowVersions(showVersions === policy.id ? null : policy.id)}
                                className="border-0 bg-transparent p-0 text-xs text-blue-600 shadow-none hover:underline mt-1"
                              >
                                {showVersions === policy.id
                                  ? t('governance.policies.hideVersions', 'Hide versions')
                                  : t('governance.policies.showVersions', `Show ${policy._count.versions} version(s)`)}
                              </Button>
                            )}
                          </td>
                          <td className={listStyles.td}>{policy.code || '—'}</td>
                          <td className={listStyles.td}>{getTypeLabel(policy.type)}</td>
                          <td className={listStyles.td}>
                            <div className={listStyles.actionsGap}>
                              <span className={`${listStyles.badge} ${getPolicyStatusBadgeClass(policy.status)}`}>
                                {policy.status}
                              </span>
                              {policy.currentVersionId ? (
                                <GovernanceDocumentIndexStatusBadge
                                  sourceType="POLICY"
                                  sourceEntityType="governance_policy_version"
                                  sourceEntityId={policy.currentVersionId}
                                />
                              ) : null}
                            </div>
                          </td>
                          <td className={listStyles.td}>{policy.department || '—'}</td>
                          <td className={listStyles.td}>{policy.ownerName || '—'}</td>
                          <td className={listStyles.td}>
                            {policy.effectiveFrom ? new Date(policy.effectiveFrom).toLocaleDateString() : '—'}
                          </td>
                          <td className={listStyles.td}>
                            {nextReview ? (
                              <div className={listStyles.actionsGap}>
                                <Calendar className={listStyles.badgeIcon} aria-hidden />
                                <span>{new Date(nextReview.plannedDate).toLocaleDateString()}</span>
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          {featureFlags.DOCUMENT_LIFECYCLE_V1 ? (
                            <td className={listStyles.td}>
                              {effectiveToDate
                                ? new Date(effectiveToDate).toLocaleDateString()
                                : '—'}
                            </td>
                          ) : null}
                          <td className={`${listStyles.td} ${listStyles.tableActionsCell}`}>
                            <div className={listStyles.actionsGap}>
                              <Button
                                type="button"
                                size="small"
                                color="outline"
                                iconOnly
                                icon={<Edit className={listStyles.badgeIcon} aria-hidden />}
                                onClick={() => setEditPolicy(policy)}
                                className="border-0 bg-transparent p-1 text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800 rounded"
                                title={t('governance.documentLifecycle.editDocument', 'Edit document')}
                              />
                              <Button
                                type="button"
                                size="small"
                                color="outline"
                                iconOnly
                                icon={<Upload className={listStyles.badgeIcon} aria-hidden />}
                                onClick={() => {
                                  setUploadPolicyId(policy.id)
                                  setUploadPolicyTitle(policy.title)
                                  setUploadVersionModalOpen(true)
                                }}
                                className="border-0 bg-transparent p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                                title={t('governance.policies.uploadNewVersion', 'Upload new version')}
                              />
                              {policy.versions.length > 0 && (
                                <Button
                                  type="button"
                                  size="small"
                                  color="outline"
                                  iconOnly
                                  icon={<Download className={listStyles.badgeIcon} aria-hidden />}
                                  onClick={() => handleDownloadVersion(policy.id, policy.versions[0].id)}
                                  className="border-0 bg-transparent p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded"
                                  title={t('governance.policies.downloadLatest', 'Download latest version')}
                                />
                              )}
                              <Button
                                type="button"
                                size="small"
                                color="outline"
                                iconOnly
                                icon={<Eye className={listStyles.badgeIcon} aria-hidden />}
                                onClick={() => setShowReviews(showReviews === policy.id ? null : policy.id)}
                                className="border-0 bg-transparent p-1 text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                                title={t('governance.policies.viewReviews', 'View reviews')}
                              />
                              <Button
                                type="button"
                                size="small"
                                color="outline"
                                iconOnly
                                icon={<Trash2 className={listStyles.badgeIcon} aria-hidden />}
                                onClick={() => handleDeletePolicy(policy.id)}
                                className="border-0 bg-transparent p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                title={t('governance.policies.delete', 'Delete')}
                              />
                            </div>
                          </td>
                        </tr>
                        {/* Versions row */}
                        {showVersions === policy.id && (
                          <tr className={listStyles.row}>
                            <td colSpan={featureFlags.DOCUMENT_LIFECYCLE_V1 ? 11 : 9} className={listStyles.nestedCell}>
                              <h4 className="font-semibold mb-2">
                                {t('governance.documentLifecycle.versionHistory', 'Version history')}
                              </h4>
                              <PolicyDocumentVersionHistory
                                policyId={policy.id}
                                versions={versionHistoryByPolicy[policy.id] || policy.versions}
                                onDownload={handleDownloadVersion}
                              />
                            </td>
                          </tr>
                        )}
                        {/* Reviews row */}
                        {showReviews === policy.id && policy.reviews.length > 0 && (
                          <tr className={listStyles.row}>
                            <td colSpan={9} className={listStyles.nestedCell}>
                              <div className="space-y-2">
                                <h4 className="font-semibold mb-2">{t('governance.policies.reviews', 'Reviews')}</h4>
                                {policy.reviews.map((review) => (
                                  <div key={review.id} className="flex items-center justify-between p-2 bg-white dark:bg-gray-700 rounded">
                                    <div>
                                      <span className="font-medium">{new Date(review.plannedDate).toLocaleDateString()}</span>
                                      <span className={`ml-2 ${listStyles.badge} ${getPolicyStatusBadgeClass(review.status)}`}>
                                        {review.status}
                                      </span>
                                      {review.reviewerName && (
                                        <span className="text-xs text-gray-500 ml-2">{review.reviewerName}</span>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
      </GovernanceListingSection>

      {uploadPolicyId && (
        <UploadVersionModal
          open={uploadVersionModalOpen}
          onClose={() => {
            setUploadVersionModalOpen(false)
            setUploadPolicyId(null)
            setUploadPolicyTitle('')
          }}
          policyId={uploadPolicyId}
          policyTitle={uploadPolicyTitle}
          onSuccess={loadPolicies}
        />
      )}
    </PageLayout>
  )
}

export default GovernancePolicies
